<template>
    <GoogleMap
        ref="mapRef"
        class="gmap md:pb-0"
        mapId="ValleyMap"
        :api-key="googleApiKey"
        :api-promise="apiPromise"
        :center="mapCenter"
        :zoom="zoom"
        :libraries="['places', 'geometry']"
        :style="{height: mapHeight, width: '100%'}"
    >
        <!-- User location marker -->

        <!-- render markers -->
        <template v-for="marker in mapMarkers">
            <AdvancedMarker :options="marker.core_options" @click="$emit('clickedMarker', marker)">
                <template #content>
                    <MapCircle />
                </template>
            </AdvancedMarker>
        </template>

        <!-- render lines -->
        <template v-for="line in mapLines">
            <Polyline :options="line"/>
        </template>
    </GoogleMap>
</template>

<script>
import {usePage} from '@inertiajs/vue3';
import {defineComponent, ref, watch} from "vue";
import {GoogleMap, AdvancedMarker, Polyline} from "vue3-google-map";
import {loadGoogleJavascript} from "../utils/google";
import MapCircle from "./Markers/MapCircle.vue";

export default defineComponent({
    name: "ValleyMap",
    components: {
        MapCircle,
        GoogleMap,
        Polyline,
        AdvancedMarker,
    },
    emits: ['ready', 'clickedMarker'],
    props: {
        zoom: {
            type: Number,
            default: 11,
        },
        mapMarkers: {
            type: Array,
            required: false,
        },
        mapLines: {
            type: Array,
            required: false,
        },
        mapCenter: {
            type: Object,
            required: false,
            default: {lat: 33.451284, lng: -112.07454}
        },
        mapHeight: {
            type: String,
            required: false,
            default: '500px',
        },
    },

    setup(props, {emit}) {
        const mapRef = ref(null);
        const googleApiKey = usePage().props.global.google.maps_api_key;
        const apiPromise = ref(null);

        apiPromise.value = loadGoogleJavascript(googleApiKey);

        // Emit an event when the Google maps library is ready.
        watch(() => mapRef.value?.ready, () => {
            if (mapRef.value.ready) {
                emit('ready', mapRef);
            }
        });

        watch(() => props.mapCenter, (to) => {
            setCenter(to);
        });

        watch(() => props.mapZoom, (zoom) => {
            setZoom(zoom);
        });

        const panTo = ({lat, lng}) => {
            mapRef.value?.map.panTo({lat, lng});
        };

        const infoWindows = new Map();

        const showInfoWindow = (key, content, position) => {
            if (!infoWindows[key]) {
                const infoWindow = new google.maps.InfoWindow({
                    content,
                    position,
                    pixelOffset: new google.maps.Size(0, -10),
                });

                infoWindows.set(key, infoWindow);
            }

            infoWindows.get(key).open({
                map: mapRef.value?.map,
                shouldFocus: false
            });
        };

        const clearInfoWindows = () => {
            infoWindows.forEach((value, key) => {
                value.close();
            });
        };

        const fitToMarkers = () => {
            const bounds = new google.maps.LatLngBounds()

            props.mapMarkers.forEach(marker => {
                bounds.extend(marker.core_options.position);
            });

            mapRef.value?.map.setCenter(bounds.getCenter());
            mapRef.value?.map.fitBounds(bounds);
            mapRef.value?.map.setZoom(props.mapZoom);
        };

        const setZoom = (zoom) => {
            mapRef.value?.map?.setZoom(zoom);
        }

        const setCenter = (coordinates) => {
            mapRef.value?.map?.setCenter(coordinates);
        };

        return {
            mapRef,
            mapCenter: props.mapCenter,
            googleApiKey,
            // External/public methods
            panTo,
            showInfoWindow,
            clearInfoWindows,
            fitToMarkers,
            setCenter,
            setZoom,
            apiPromise,
        };
    }
})
</script>

<style>
@media (max-width: 639px) {
    .gmap .mapdiv {
        height: 500px !important;
    }
}
</style>
